import { Controller } from "@hotwired/stimulus"

// "Enable notifications on this device" on the contractor leads dashboard —
// see ContractorLeadNotificationJob (server side) and
// app/views/pwa/service-worker.js (the push/notificationclick handlers).
// Rendered only when WebPushConfig.configured? (see _notifications_card).
//
// The browser's PushManager subscription is one per origin, not per
// tenant — a device that's already subscribed under tenant A (or under a
// different user on a shared browser) is NOT "enabled" for tenant B, so
// enabled/disabled state is never inferred from browser state alone. Every
// render asks the server (POST .../push_subscriptions/status) what's true
// for the *current* user+tenant.
export default class extends Controller {
  static values = { vapidPublicKey: String, createUrl: String, statusUrl: String }
  static targets = ["status", "enableButton", "disableButton", "takeoverButton"]

  connect() {
    this.refreshState()
  }

  async refreshState() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.setStatus("Push notifications aren't supported in this browser.")
      return
    }
    if (Notification.permission === "denied") {
      this.setStatus("Notifications are blocked for this site — check your browser's site settings.")
      return
    }

    try {
      // Ask the server for eligibility BEFORE touching the browser's
      // PushManager subscription at all — status with no endpoint returns
      // eligible (workspace membership) without needing one. Skipping this
      // and inferring `eligible: true` from "no browser subscription yet"
      // (the old behavior) offered Enable to admins/non-members too: a
      // click would create a real browser subscription and then 403 on
      // #create, potentially orphaning it.
      const eligibility = await this.fetchStatus()
      if (!eligibility.eligible) {
        this.render(eligibility)
        return
      }

      const registration = await navigator.serviceWorker.register("/service-worker", { scope: "/" })
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        this.render({ eligible: true, registered: false })
        return
      }
      if (!this.subscriptionKeyMatches(subscription)) {
        // A VAPID key rotation since this device last subscribed leaves the
        // subscription bound to the old key — pushes to it fail silently
        // now, so "enabled" here would be a lie. Show Enable instead so the
        // visitor has a way to recover; #enable is what actually discards
        // the stale subscription and re-registers under the new key.
        this.render({ eligible: true, registered: false })
        return
      }
      const status = await this.fetchStatus(subscription.endpoint)
      this.render(status)
    } catch {
      this.setStatus("Couldn't check notification status on this device.")
    }
  }

  async fetchStatus(endpoint) {
    // statusUrlValue may already carry a ?tenant=... query string (a
    // multi-tenant user) — leave that alone, it's just a slug. The push
    // endpoint itself is a bearer-style URL (anyone holding it can push to
    // that browser), so it goes in the POST body instead of the query
    // string, where it'd otherwise end up in server access logs, browser
    // history, and Referer headers. endpoint is omitted entirely for the
    // eligibility-only check above — the server treats a missing endpoint
    // as "not registered" and still reports real eligibility.
    const response = await fetch(this.statusUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), "Content-Type": "application/json" },
      body: JSON.stringify(endpoint ? { endpoint } : {}),
    })
    if (!response.ok) throw new Error("status check failed")
    return response.json()
  }

  render({ eligible, registered }) {
    // Every normal state transition supersedes a pending takeover offer —
    // clear it so a stale reference from an earlier prompt can't be reused
    // by a later #takeover click.
    this.pendingTakeoverSubscription = null

    if (!eligible) {
      this.enableButtonTarget.classList.add("hidden")
      this.disableButtonTarget.classList.add("hidden")
      this.takeoverButtonTarget.classList.add("hidden")
      this.setStatus("Notifications are only available to workspace members.")
      return
    }
    this.enableButtonTarget.classList.toggle("hidden", registered)
    this.disableButtonTarget.classList.toggle("hidden", !registered)
    this.takeoverButtonTarget.classList.add("hidden")
    this.setStatus(registered ? "Notifications are enabled on this device." : "Notifications are off on this device.")
  }

  async enable() {
    const permission = await Notification.requestPermission()
    if (permission !== "granted") {
      this.setStatus("Notifications are blocked for this site — check your browser's site settings.")
      return
    }

    let subscription
    try {
      const registration = await navigator.serviceWorker.register("/service-worker", { scope: "/" })
      subscription = await registration.pushManager.getSubscription()
      if (subscription && !this.subscriptionKeyMatches(subscription)) {
        // A VAPID key rotation since this device last subscribed leaves the
        // browser holding a subscription bound to the OLD key — reusing it
        // would save it again and report success while pushes (now signed
        // with the new key) keep failing. That old endpoint is bound to a
        // key nobody can push with anymore, so it's useless to every
        // account that's ever registered it, not just this one: tear down
        // the browser subscription and this user's own row for it, then
        // subscribe fresh below.
        await this.discardStaleSubscription(subscription)
        subscription = null
      }
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue),
        })
      }
      const response = await this.postSubscription(subscription)
      if (response.status === 409) {
        const body = await response.json().catch(() => ({}))
        if (body.code === "endpoint_registered_to_another_member" && body.takeover_allowed) {
          // This browser's one-per-origin subscription is a perfectly
          // good, correctly-keyed registration — it just belongs
          // (server-side) to a different member of this same team.
          // Nothing to tear down here; only the confirmed takeover click
          // below decides to reassign it.
          this.pendingTakeoverSubscription = subscription
          this.showTakeoverPrompt()
          return
        }
      }
      if (!response.ok) throw new Error("subscription save failed")

      this.render({ eligible: true, registered: true })
    } catch {
      // `subscribe()` above may have just created a brand-new browser
      // PushManager subscription (not merely reused an existing one) right
      // before #create rejected it (e.g. a 403 from a membership check that
      // changed between the eligibility check and this click) — leaving it
      // behind orphans a subscription this account can never actually use.
      // Only torn down when nothing else still references it, the same
      // cross-tenant/cross-user guard #disable uses.
      if (subscription) {
        try {
          const after = await this.fetchStatus(subscription.endpoint)
          if (!after.endpoint_still_registered) await subscription.unsubscribe()
        } catch {
          // Best-effort cleanup — the failure reported below is the one that matters.
        }
      }
      this.setStatus("Couldn't enable notifications — try again.")
    }
  }

  // Confirmed handoff for a shared/kiosk browser: this person clicked
  // "Switch them to you" after #enable's 409 above, so re-post the SAME
  // browser subscription with takeover: true and, on success, proceed as a
  // normal successful enable. The browser subscription itself is never
  // touched here — only server-side ownership of the row changes.
  async takeover() {
    const subscription = this.pendingTakeoverSubscription
    if (!subscription) return

    try {
      const response = await this.postSubscription(subscription, { takeover: true })
      if (response.status === 422) {
        // The device-limit rejection (see #create) carries a message worth
        // showing as-is — "try again" would be actively misleading, since
        // retrying the same takeover click can't succeed until a device is
        // freed up.
        const body = await response.json().catch(() => ({}))
        this.setStatus(body.errors?.[0] ?? "Couldn't switch notifications to you — try again.")
        return
      }
      if (!response.ok) throw new Error("takeover failed")

      this.pendingTakeoverSubscription = null
      this.render({ eligible: true, registered: true })
    } catch {
      this.setStatus("Couldn't switch notifications to you — try again.")
    }
  }

  async postSubscription(subscription, { takeover = false } = {}) {
    const keys = subscription.toJSON().keys
    return fetch(this.createUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken() },
      body: JSON.stringify({
        push_subscription: { endpoint: subscription.endpoint, p256dh: keys.p256dh, auth: keys.auth },
        takeover,
      }),
    })
  }

  showTakeoverPrompt() {
    this.enableButtonTarget.classList.add("hidden")
    this.disableButtonTarget.classList.add("hidden")
    this.takeoverButtonTarget.classList.remove("hidden")
    this.setStatus("This browser gets notifications for another team member.")
  }

  async disable() {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/")
      const subscription = registration && (await registration.pushManager.getSubscription())
      if (!subscription) {
        this.render({ eligible: true, registered: false })
        return
      }

      const status = await this.fetchStatus(subscription.endpoint)
      if (status.destroy_url) {
        const response = await fetch(status.destroy_url, { method: "DELETE", headers: { "X-CSRF-Token": this.csrfToken() } })
        if (!response.ok) throw new Error("unsubscribe failed")
      }

      // Only tear down the browser's one-per-origin PushManager subscription
      // once nothing — no tenant, no other user on this browser (a shared/
      // kiosk device) — still references it. endpoint_still_registered
      // covers every account, not just the current one, so disabling for
      // this tenant never silently disables another tenant/user sharing
      // this device.
      const after = await this.fetchStatus(subscription.endpoint)
      if (!after.endpoint_still_registered) {
        await subscription.unsubscribe()
      }

      this.render({ eligible: true, registered: false })
    } catch {
      this.setStatus("Couldn't disable notifications — try again.")
    }
  }

  // Compares the ArrayBuffer key a subscription was actually created with
  // against the currently configured public key, byte-for-byte — a
  // mismatch means the subscription predates a VAPID key rotation and any
  // push to it will fail. `subscription.options` (and its
  // applicationServerKey) can be unavailable on some browsers/mocks; treat
  // that as a mismatch too rather than assuming it's still good.
  subscriptionKeyMatches(subscription) {
    const key = subscription.options?.applicationServerKey
    if (!key) return false
    const configured = this.urlBase64ToUint8Array(this.vapidPublicKeyValue)
    const actual = new Uint8Array(key)
    if (actual.length !== configured.length) return false
    return actual.every((byte, i) => byte === configured[i])
  }

  // Tears down a browser PushManager subscription bound to a rotated VAPID
  // key, and removes this user's own row for its endpoint — a key mismatch
  // means the endpoint can never receive a push again for ANY account that
  // registered it, so there's nothing to preserve by keeping either around.
  // Best-effort: even if the server-side row can't be removed (e.g. a
  // network error), the stale browser subscription still needs to go so
  // #enable's fresh subscribe() below isn't blocked by it.
  async discardStaleSubscription(subscription) {
    try {
      const status = await this.fetchStatus(subscription.endpoint)
      if (status.destroy_url) {
        await fetch(status.destroy_url, { method: "DELETE", headers: { "X-CSRF-Token": this.csrfToken() } })
      }
    } catch {
      // See comment above — the unsubscribe below still has to happen.
    } finally {
      await subscription.unsubscribe()
    }
  }

  setStatus(text) {
    this.statusTarget.textContent = text
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
  }
}
